import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { useIsModeDisabledByIncognito } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeDisabledByIncognito.js';
import { useIsModeDisabledByOrg } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeDisabledByOrg.js';
import { useSearchModeGateInfo } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSearchModeGateInfo.js';
import { sidecarSearchModeSelectedEvent } from '#pplx/frontend/apps/ask/search-modes-menu/analytics.js';
import { useVisibleSearchModes } from '#pplx/frontend/apps/ask/search-modes-menu/useVisibleSearchModes.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { getSearchModeIcon, isSearchMode, SearchMode, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const LazyAskInputSearchModeUpsellPopover = Dynamic(async () => {
    const { AskInputSearchModeUpsellPopover } = await import('#pplx/frontend/apps/ask/ask-input/ask-input-upsell-popover/components/AskInputSearchModeUpsellPopover.js');
    return { default: AskInputSearchModeUpsellPopover };
}, { loading: ({ children }) => _jsx(_Fragment, { children: children }) });
export const SearchModeMenuItems = memo(function SearchModeMenuItems({ searchMode: selectedSearchMode, onSearchModeSelect, omittedSearchModes = EMPTY_ARRAY, tooltipMaxWidth, submenuMaxWidth, primaryModes, closeOnSelect: closeOnSelectProp = true, }) {
    useIntl();
    const isSidecar = !!import.meta.env.VITE_IS_SIDECAR_BUILD;
    const modes = useVisibleSearchModes({ omittedSearchModes });
    const { topLevelModes, submenuModes } = useMemo(() => {
        if (!primaryModes) {
            return { topLevelModes: modes, submenuModes: [] };
        }
        // Sort top-level modes to match the order specified in primaryModes
        const topLevel = primaryModes
            .map((id) => modes.find((m) => m.id === id))
            .filter((m) => m != null);
        return {
            topLevelModes: topLevel,
            submenuModes: modes.filter((m) => !primaryModes.includes(m.id)),
        };
    }, [modes, primaryModes]);
    const openPaywallUpsell = usePaywallUpsell();
    const { gateSearchModeSelection } = useGateSearchModelSelection({
        openPaywallUpsell,
    });
    const { getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const getSearchModeGateInfo = useSearchModeGateInfo();
    const { track } = useInstrumentation();
    const handleSearchModeSelect = (value) => {
        if (!isSearchMode(value)) {
            logger.error(`Invalid search mode selected: ${value}`);
            return;
        }
        const searchModel = getPreferredSearchModelForSearchMode(value);
        const gated = gateSearchModeSelection(searchModel);
        if (gated) {
            return;
        }
        if (isSidecar) {
            track(sidecarSearchModeSelectedEvent({
                name: 'sidecar search mode selected',
                data: { previousMode: selectedSearchMode, selectedMode: value },
            }));
        }
        onSearchModeSelect(value);
    };
    return (_jsxs(DropdownMenu.RadioGroup, { value: selectedSearchMode, onValueChange: handleSearchModeSelect, closeOnSelect: closeOnSelectProp, children: [topLevelModes.map((mode) => (_jsx(SearchModeMenuItem, { mode: mode, tooltipMaxWidth: tooltipMaxWidth, gateInfo: getSearchModeGateInfo(mode.id), onSelect: handleSearchModeSelect }, mode.id))), submenuModes.length > 0 && (_jsx(DropdownMenu.Submenu, { maxWidthPx: submenuMaxWidth, triggerElement: _jsx(DropdownMenu.SubmenuItem, { leadingAccessory: _jsx(Icon, { icon: $icon('dots'), size: "small" }), children: _jsx(FormattedMessage, { defaultMessage: "More", id: "a62eafc624", description: "Submenu item label for additional search modes" }) }), children: submenuModes.map((mode) => (_jsx(SearchModeMenuItem, { mode: mode, tooltipMaxWidth: tooltipMaxWidth, gateInfo: getSearchModeGateInfo(mode.id), onSelect: handleSearchModeSelect }, mode.id))) }))] }));
});
const SearchModeMenuItem = ({ mode, tooltipMaxWidth, gateInfo, onSelect, }) => {
    const { $t } = useIntl();
    const isModeDisabledByOrg = useIsModeDisabledByOrg();
    const isDisabledByOrg = isModeDisabledByOrg(mode.id);
    const isModeDisabledByIncognito = useIsModeDisabledByIncognito();
    const isDisabledByIncognito = isModeDisabledByIncognito(mode.id);
    const { isMobileUserAgent } = useAetherContext();
    const IconComponent = getSearchModeIcon(mode.id);
    const isGated = gateInfo.isGated;
    const isDisabled = isDisabledByOrg || isDisabledByIncognito;
    const badge = useMemo(() => {
        if (mode.badge) {
            return mode.badge;
        }
        if (mode.subscriptionTier === 'max') {
            return 'Max';
        }
        return undefined;
    }, [mode]);
    const badgeVariant = badge === 'Max' ? 'neutral' : undefined;
    const handleGatedSelect = useCallback(() => {
        onSelect(mode.id);
    }, [onSelect, mode.id]);
    const disabledMessage = useMemo(() => {
        if (isDisabledByOrg) {
            return $t({
                defaultMessage: 'Disabled by your organization',
                id: 'e46057f35d',
                description: 'Message shown when a search mode is disabled by organization policy',
            });
        }
        if (isDisabledByIncognito) {
            return $t({
                defaultMessage: 'Disabled in incognito',
                id: 'bba557061c',
                description: 'Message shown when a search mode is disabled in incognito/OTR browser profile',
            });
        }
        return undefined;
    }, [isDisabledByOrg, isDisabledByIncognito, $t]);
    const isSidecar = !!import.meta.env.VITE_IS_SIDECAR_BUILD;
    const tooltipContent = disabledMessage || mode.subtitle;
    const showTooltipAsSubtitle = isSidecar && !!tooltipContent;
    if (isGated && !isDisabled) {
        const menuItem = (_jsx("div", { className: "group/search-mode", children: _jsx(DropdownMenu.Item, { onSelect: handleGatedSelect, leadingAccessory: _jsx(Icon, { icon: IconComponent, size: "small" }), trailingAccessory: _jsx(Icon, { icon: $icon('lock'), size: "small", color: "light" }), badge: badge, badgeVariant: badgeVariant, children: _jsx("span", { className: "group-hover/search-mode:text-primary group-hover/search-mode:[&>*]:!text-inherit transition-colors", children: _jsx(TextPrimitive, { size: "sm", color: "light", children: mode.label }) }) }) }));
        if (isMobileUserAgent) {
            return menuItem;
        }
        return (_jsx(LazyAskInputSearchModeUpsellPopover, { searchMode: mode.id, gateReason: gateInfo.reason, showMaxUpsell: gateInfo.showMaxUpsell, subscriptionTier: mode.subscriptionTier, onClick: handleGatedSelect, children: menuItem }));
    }
    return (_jsx(Tooltip, { content: tooltipContent ?? '', side: "right", maxWidthPx: tooltipMaxWidth, disabled: isMobileUserAgent || !tooltipContent || showTooltipAsSubtitle, children: _jsx(DropdownMenu.RadioItem, { value: mode.id, leadingAccessory: _jsx(Icon, { icon: IconComponent, size: "small" }), disabled: isDisabled, badge: badge, badgeVariant: badgeVariant, subtitle: showTooltipAsSubtitle ? tooltipContent : undefined, children: mode.label }) }));
};
//# sourceMappingURL=SearchModeMenuItems.js.map